import classnames from 'classnames';
import { RawDraftContentState } from 'draft-js';
import { isNull, isUndefined, max } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch } from '../../../hooks/react-redux';
import { riskAssociatedLabel, riskAssociatedColors, riskLevels } from '../../admin/risk-tolerance/utils';
import { DeleteButton } from '../../shared/button/DeleteButton';
import { WYSIWYG } from '../../shared/wysiwyg/WYSIWYG';
import styles from './DatasetInstance.module.scss';
import { DocumentFieldRiskOverride, setCurrentClientRiskConversation, ClientRiskConversation } from './store';
import { CustomTooltip } from '../../shared/tooltip';
import { DatasetFieldType } from '../store';
import { DropdownRiskConfig, CheckboxRiskConfig, DateRiskConfig, NumberRiskConfig, TimeRiskConfig, CurrencyRiskConfig, RiskAssociated, TextRiskConfig, CalculatorRiskConfig } from '../../admin/risk-tolerance/store';

interface DocumentRiskOverrideInputProps {
    justification: RawDraftContentState | null;
    overrideRiskLevel: number | undefined;
    updateJustification: (value: RawDraftContentState) => void;
    clientRiskConversation: ClientRiskConversation[];
    documentRiskOverride: DocumentFieldRiskOverride;
}

export const DocumentRiskOverrideInput: React.FC<DocumentRiskOverrideInputProps> = ({ justification, updateJustification, overrideRiskLevel, clientRiskConversation, documentRiskOverride }) => {
    const dispatch = useAppDispatch();
    const { fieldRiskConfig, fieldType } = documentRiskOverride;

    const highestRiskLevelAvailable = useMemo(() => {
        let highestRisk = 0;
        if (!isUndefined(fieldRiskConfig)) {
            const { fallbackRiskPosition, riskConfig } = fieldRiskConfig;
            riskLevels.map(riskLevel => {
                if (fieldType !== DatasetFieldType.CURRENCY_AMOUNT && !isUndefined(riskConfig[riskLevel])) {
                    if (fieldType === DatasetFieldType.DROPDOWN && (riskConfig as DropdownRiskConfig)[riskLevel].length > 0) {
                        highestRisk = riskLevel;
                    }
                    if (fieldType === DatasetFieldType.CHECKBOX && !isUndefined((riskConfig as CheckboxRiskConfig)[riskLevel])) {
                        highestRisk = riskLevel;
                    }
                    if (fieldType === DatasetFieldType.DATE && !isNull((riskConfig as DateRiskConfig)[riskLevel].value) && !isNull((riskConfig as DateRiskConfig)[riskLevel].comparator)) {
                        highestRisk = riskLevel;
                    }
                    if (fieldType === DatasetFieldType.NUMBER && !isUndefined((riskConfig as NumberRiskConfig)[riskLevel].value) && !isNull((riskConfig as NumberRiskConfig)[riskLevel].comparator)) {
                        highestRisk = riskLevel;
                    }
                    if (fieldType === DatasetFieldType.TIME && (riskConfig as TimeRiskConfig)[riskLevel].value.every(val => val.length) && !isNull((riskConfig as TimeRiskConfig)[riskLevel].comparator)) {
                        highestRisk = riskLevel;
                    }
                    if ([DatasetFieldType.TEXT, DatasetFieldType.LONGTEXT].includes(fieldType) && (riskConfig as TextRiskConfig)[riskLevel].length > 0) {
                        highestRisk = riskLevel;
                    }
                    if (fieldType === DatasetFieldType.CALCULATOR && (riskConfig as CalculatorRiskConfig)[riskLevel].length > 0) {
                        highestRisk = riskLevel;
                    }
                }
                if (fieldType === DatasetFieldType.CURRENCY_AMOUNT) {
                    const currencyConfigs = Object.keys(riskConfig);
                    // If we have configurations set up we then need to go through all of them and work out what the worst possible highestRisk is between them all
                    if (currencyConfigs.length > 0) {
                        if (currencyConfigs.some(currency => !isUndefined((riskConfig as CurrencyRiskConfig)[currency][riskLevel].value) && !isNull((riskConfig as CurrencyRiskConfig)[currency][riskLevel].comparator))) {
                            highestRisk = riskLevel;
                        }
                    }
                }
            });
            highestRisk = max([highestRisk, fallbackRiskPosition])!;
        }
        return highestRisk;
    }, [fieldType, fieldRiskConfig]);

    const riskLevelWrapperStyle = useMemo(() => !isUndefined(overrideRiskLevel) ? { width: 'calc(20% - 10px)' } : { width: '100%' }, [overrideRiskLevel]);
    const getIsDisabled = useCallback((riskAssociated: number) => (clientRiskConversation.length > 0 && clientRiskConversation[clientRiskConversation.length - 1].overrideRiskLevel === riskAssociated) || (riskAssociated > highestRiskLevelAvailable), [clientRiskConversation, highestRiskLevelAvailable]);

    const selectRiskToleranceValue = useCallback((value: number | undefined) => dispatch(setCurrentClientRiskConversation({ justification, overrideRiskLevel: value })), [dispatch, justification]);
    const resetRiskToleranceValue = useCallback(() => selectRiskToleranceValue(undefined), [selectRiskToleranceValue]);
    const riskToleranceIconDisplay = useMemo(() => {
        if (!isUndefined(overrideRiskLevel)) {
            return (
                <div className={styles.selectedIndicatorWrapper}>
                    <div className={styles.selectedIndicator}>
                        <div className={styles.riskAssociatedLabel} style={{ backgroundColor: riskAssociatedColors[overrideRiskLevel as RiskAssociated] }}>
                            {riskAssociatedLabel[overrideRiskLevel as RiskAssociated]}
                        </div>
                    </div>
                    <div className={styles.deleteButtonWrapper}><DeleteButton onClick={resetRiskToleranceValue} fontSize={18} /></div>
                </div>
            );
        }
        return (Object.values(RiskAssociated).slice(5) as RiskAssociated[]).map(riskAssociated => {
            const disabled = getIsDisabled(riskAssociated);
            const tooltipContent = disabled ? `Risk already set to ${riskAssociatedLabel[riskAssociated]}` : null;
            return (
                <button className={styles.indicatorWrapper} onClick={() => selectRiskToleranceValue(riskAssociated)} key={riskAssociated} disabled={disabled}>
                    <CustomTooltip overlayText={tooltipContent} placement='top'>
                        <div className={styles.riskAssociatedLabel} style={{ backgroundColor: riskAssociatedColors[riskAssociated] }}>
                            {riskAssociatedLabel[riskAssociated]}
                        </div>
                    </CustomTooltip>
                </button>
            );
        });
    }, [overrideRiskLevel, selectRiskToleranceValue, resetRiskToleranceValue, getIsDisabled]);

    return (
        <div className={styles.editableNoteWrapper}>
            <div className={classnames(styles.smileyWrapper, { [styles.selectedRiskToleranceLevel]: !isUndefined(overrideRiskLevel) })} style={riskLevelWrapperStyle}>
                <div className={classnames(styles.justificationIndicator, { [styles.addedHeight]: isUndefined(overrideRiskLevel) })}>
                    {riskToleranceIconDisplay}
                </div>
            </div>
            {!isUndefined(overrideRiskLevel) &&
                <WYSIWYG
                    content={justification}
                    updateContent={updateJustification}
                    showWrapperBorder={false}
                    padding='0'
                />
            }
        </div>
    );
};
